import {
  sendToUnityWithResponse,
  type UnitySendOptions,
} from "../../components/Unityboards/UnityBridge";

import { areAdvancedEditorFeaturesEnabled } from "./editorFeatureAvailability";

export type UnityToolCommandSender = (
  command: string,
  payload?: Record<string, unknown>,
  options?: UnitySendOptions,
) => Promise<void>;

function withToolCommandSendOptions(
  command: string,
  payload: Record<string, unknown>,
  options: UnitySendOptions,
): UnitySendOptions {
  const transactionId =
    typeof payload.transactionId === "string" && payload.transactionId.trim()
      ? payload.transactionId
      : null;
  if (!transactionId) {
    return options;
  }
  if (command === "tool_addSpace" || command === "tool_duplicateSpace") {
    return {
      ...options,
      transactionId: options.transactionId ?? transactionId,
      requiresReadyState: options.requiresReadyState ?? "booting",
      dedupeKey: options.dedupeKey ?? `reactToUnity_command::${transactionId}`,
    };
  }
  if (command === "tool_cancelSpaceTransaction") {
    // 이미 abort한 transaction을 Unity에 알리는 명령이라 stale 검사에 걸리면 안 된다.
    return {
      ...options,
      requiresReadyState: options.requiresReadyState ?? "handlers_ready",
      skipStaleTransactionCheck: true,
    };
  }
  if (command === "tool_reorderSpace") {
    return {
      ...options,
      transactionId: options.transactionId ?? transactionId,
      requiresReadyState: options.requiresReadyState ?? "handlers_ready",
      dedupeKey: options.dedupeKey ?? `reactToUnity_command::${transactionId}`,
      skipStaleTransactionCheck: options.skipStaleTransactionCheck ?? true,
    };
  }
  return options;
}

export function createUnityToolCommandAdapter(
  sendToUnity: typeof sendToUnityWithResponse = sendToUnityWithResponse,
): UnityToolCommandSender {
  return (command, payload = {}, options = {}) => {
    if (!areAdvancedEditorFeaturesEnabled() &&
        ["measure_linear", "measure_line", "measure_keep_visible"].includes(command)) {
      return Promise.resolve();
    }
    const sendOptions = withToolCommandSendOptions(command, payload, options);
    return sendToUnity(
      "reactToUnity_command",
      {
        command,
        ...payload,
      },
      sendOptions,
    );
  };
}

export const sendUnityToolCommand = createUnityToolCommandAdapter();
